import { Pipe, PipeTransform } from '@angular/core';
import {
  IBudgetAllocForm,
  IItemToRequest,
  MyPurchaseForm
} from 'src/app/shared/models/app-models/MyPurchaseForm.model';
import {
  Classification,
  ExtendedMaterialGroup,
  ExtendedMaterialSubGroup,
  StoreDropdowns,
} from 'src/app/shared/models/app-models';
import { AddOn, SaveMyPurchase } from 'src/app/shared/models/database-models';
import {
  BudgetAllocation,
  Item,
  TaggedGroup,
  Vendor,
} from 'src/app/shared/models/database-models/SaveMyPurchase.model';
import classification from 'src/app/data/constants/Classification.json';
import WorkmanshipStandard from 'src/app/data/constants/WorkmanshipStandard.json';
import TypeOfRequisitions from 'src/app/data/constants/TypeOfRequisition.json';
import { ExtendedWOCReason } from '../../models/app-models/ExtendedWOCReason.model';
import { ExtendedDOTPReason } from '../../models/app-models/ExtendedDOTPReason.model';
import { ExcoBoardDocumentRecord } from 'src/app/shared/models/app-models/ExcoBoardDocument.model';
import { ExtendedReasonForNotUsingRFQ } from '../../models/app-models/ExtendedReasonForNotUsingRFQ.model';
import { environment } from 'src/environments/environment';
import { checkIfProjectRORequired } from 'src/app/modules/shared/utils/derive-budget-allocations';
import { itemListFrontendToBackendMapper } from 'src/app/modules/shared/utils/format-form-data-for-save';
import { PurchaseTypeMap, PurchaseType } from "src/app/shared/models/app-models/Approving.model";
import { GroupStatus, TaggedGroup as ParsedTaggedGroup, TaggedGroups } from 'src/app/modules/mypurchase-form/rfs/tag-item-vendor.service';
import { SourcingStatusId as SourcingStatusIdEnum } from '../../models/database-models/SourcingStatus';
import { DocumentStatusToRemoveDIRP, DocumentSubStatusToRemoveDIRP } from '../../models/database-models/DocumentStatus.model';
import { CurrentWorkflowService } from 'src/app/modules/shared/current-workflow/current-workflow.service';
import { CurrencyService } from '../../services/currency.service';

@Pipe({
  name: 'formTransformer',
})

/**
 *@abcd
 *Pipe used to transform frontend MyPurchase Form to Data format required by backend.
 *
 *Provides an Inverse function to transform data from backend to frontend too.
 *
 */
export class FormTransformerPipe implements PipeTransform {
  constructor(private currentWorkFlowService:CurrentWorkflowService, private currencyService: CurrencyService,){}
  classification: Classification[] = classification.classifications;

  // from frontend to backend
  async transform(form: MyPurchaseForm, loggedUserName: string, storeDropdowns: StoreDropdowns): Promise<SaveMyPurchase> {
    // some values are hard coded.
    //for future implementation due to unknown future requirements
    let dataToReturn: SaveMyPurchase = {
      PurchaseTypeId: form.PurchaseTypeId,
      DocumentStatusId: form.documentStatusId,
      DocumentSubStatusId: form.documentSubStatusId,
      MPDocumentNumber: form.documentNo == '' ? null : form.documentNo,
      CreatedBy: loggedUserName,
      RFQDeadline: form.RFQDeadline,
      RequestingOfficer: (form.generalForm.RO.id as string) ?? null,
      SecurityClassId: form.generalForm.internal.value,
      SecurityClassIdExt: form.generalForm.external.value,
      Purpose: form.generalForm.purpose,
      TypeOfRequisition: form.generalForm.typeOfRequisition.id,
      PurchaseTitle: form.generalForm.purchaseTitle,
      PreapprovedDocument: null, //form.generalForm.preApproved,
      AreExcoDocumentsAttached: form.generalForm.areExcoBoardDocumentsAttached,
      IsDIRP: !DocumentStatusToRemoveDIRP.includes(form.documentStatusId) && form.documentSubStatusId && !DocumentSubStatusToRemoveDIRP.includes(form.documentSubStatusId) ? form.generalForm.isDIRP : false,
      ReasonForNotUsingRFQCode: form.generalForm.reasonForNotUsingRFQ.value,
      ReasonForNotUsingRFQ: this.transformReasonForNotUsingRFQ(form.generalForm.reasonForNotUsingRFQ.value, storeDropdowns),
      ExcoBoardDocuments: this.transformExcoBoardData(form),
      MPReferenceDocument: form.parentDocumentNo ?? null,
      isBlanket: false,
      isRFS: false,
      BlanketId: null,
      isDOTP: false,
      DOTPId: null,
      isWOC: false,
      WOCId: null,
      isPermissible: false,
      PermissibleId: form.generalForm.permissibleReason ? form.generalForm.permissibleReason.id : null,
      OtherPermissibleReasons: form.generalForm.otherPermissibleReasons,
      OtherTypeOfRequisition: form.generalForm.requisitionOthers,
      itemList: [...itemListFrontendToBackendMapper(form.ItemList, form)],
      ...(form.TenderItem
        ? {
          DeliveryAddressId:
            form.TenderItem.DeliveryAddress?.id === 'others'
              ? null
              : form.TenderItem.DeliveryAddress?.id,
          TargetScheduleDate: form.TenderItem.TargetScheduleDate,
          OtherDeliveryAddress:
            form.TenderItem.DeliveryAddress?.id === 'others'
              ? form.TenderItem.PleaseSpecify
              : null,
        }
        : {}),
      vendorsList: [],
      WaiverOfCompetition: {
        WOCId: form.WOCId || null,
        WOCCode: form.WoCReason?.code || null,
        FileReferenceNumber: form.WOCFileReferenceNumber,
        WOCJustification: form.WOCJustification,
        isDeleted: false
      },
      DOTP: {
        DOTPId: form.DOTPId || null,
        DOTPReasonId: form.DoTPReason?.id || null,
        DOTPReasonName: form.DoTPReason?.Reason || null,
        DOTPJustification: form.DOTPJustification,
        DOTPRemarks: form.DOTPRemarks,
        isDeleted: false
      },
      Type: form.Type,
      Env: form.Env,
      ...(form.optionToPurchase
        ? {
          optionToPurchase:
            form.optionToPurchase?.map(({ index, ...item }) => item) || [],
        }
        : {}),
      TenderStartDate: form.TenderStartDate
        ? this.transformToDate(form.TenderStartDate, form.TenderStartTime)
        : null,
      TenderEndDate: form.TenderEndDate
        ? this.transformToDate(form.TenderEndDate, form.TenderEndTime)
        : null,
      TenderDocumentNumber: form.TenderDocumentNumber,
      isRFSEnabled: form.isRFSEnabled,
      PDPComplianceItemNumbers: form.PDPComplianceItemNumbers,
      ExchangeRateLock: form.ExchangeRateLock === null ? false: form.ExchangeRateLock,
      rfqSummaryButton:form.rfqSummaryButton
    };
    // pumping in vendors
    for (const vendor of form.VendorsToSource) {
      let vendorData: Vendor = {
        ...vendor,
        Email: (vendor.Email !== '' && vendor.Email !== '-') ? vendor.Email : null,
        OtherPersonEmail: (vendor.OtherPersonEmail !== '' && vendor.OtherPersonEmail !== '-') ? vendor.OtherPersonEmail : null,
        OtherPersonName: (vendor.OtherPersonName !== '' && vendor.OtherPersonName !== '-') ? vendor.OtherPersonName : null,
        VendorContactPerson: (vendor.VendorContactPerson !== '' && vendor.VendorContactPerson !== '-') ? vendor.VendorContactPerson : null
      };
      const { Name, ...rest } = vendorData;
      const newVendorData:Vendor = {
        ...rest
      }
      if (newVendorData.Strikethrough && newVendorData.isDeleted) {
        newVendorData.Strikethrough = false;
      }
      dataToReturn.vendorsList.push(newVendorData);
    }
    
    // let containsOneVendor : boolean = false;
    // let strikeThroughOrDeleted : number = 0;
    // for(let vendor of dataToReturn.vendorsList ){
    //   if(vendor.Strikethrough || vendor.isDeleted) strikeThroughOrDeleted++;
    // }
    // containsOneVendor = (dataToReturn.vendorsList.length - strikeThroughOrDeleted) == 1;
    
    //removing DOTP for other purchase type excluding DC
    let totalEstAmount = this.calculateTotalAmount(dataToReturn.itemList)
    console.log("totalEstAmount : ", totalEstAmount);

    const isWocApproved=this.currentWorkFlowService.getisWocApproved();
    const vendorsLength = this.calculateVendorsLength(dataToReturn.vendorsList);

    // if has WOC, 1 vendor, not yet approved, and less than 6000, then set delete flag for deletion
    if (
        dataToReturn.WaiverOfCompetition?.WOCId 
        && vendorsLength == 1
        && !isWocApproved
        && totalEstAmount < 6000
    ) {
      dataToReturn.WaiverOfCompetition.isDeleted = true;
    }

    if (
      vendorsLength !== 1 &&
      dataToReturn.WaiverOfCompetition?.WOCId && !isWocApproved
    ) {
      dataToReturn.WaiverOfCompetition.isDeleted = true;
    }

    if (
      dataToReturn.vendorsList.length === 1 &&
      dataToReturn.vendorsList[0]?.isDeleted &&
      dataToReturn.WaiverOfCompetition?.WOCId &&
      !isWocApproved
    ) {
      dataToReturn.WaiverOfCompetition.isDeleted = true;
    }

    if (!dataToReturn.WaiverOfCompetition?.WOCCode) {
      dataToReturn.WaiverOfCompetition = null
    }

    if (dataToReturn.WaiverOfCompetition?.WOCCode && !dataToReturn.WaiverOfCompetition?.isDeleted) {
      dataToReturn.isWOC = true;
    }

    if (
      dataToReturn.vendorsList.length === 1 &&
      dataToReturn.vendorsList[0]?.isDeleted &&
      dataToReturn.DOTP?.DOTPId &&
      !isWocApproved
    ) {
      dataToReturn.DOTP.isDeleted = true;
    }

    if (
      vendorsLength !== 1 &&
      dataToReturn.DOTP?.DOTPId && !isWocApproved
    ) {
      dataToReturn.DOTP.isDeleted = true;
    }

    //removing DOTP for other purchase type excluding DC
    if (dataToReturn.PurchaseTypeId != 5 || (dataToReturn.PurchaseTypeId == 5 && vendorsLength !== 1 && !dataToReturn.DOTP?.DOTPId)) {
      dataToReturn.DOTP = null;
    }

    if (dataToReturn.PurchaseTypeId == 5 && totalEstAmount <= 200000 && dataToReturn.DOTP?.DOTPId && !isWocApproved) {
      dataToReturn.DOTP.isDeleted = true;
    } else if (dataToReturn.PurchaseTypeId == 5 && totalEstAmount <= 200000) {
      dataToReturn.DOTP = null;
    }

    if (dataToReturn.DOTP?.DOTPReasonId && !dataToReturn.DOTP?.isDeleted) {
      dataToReturn.isDOTP = true;
    }

    if ((dataToReturn.PurchaseTypeId == 1 && dataToReturn.ReasonForNotUsingRFQCode) || dataToReturn.PurchaseTypeId == 3 ) {
      let isRFS = this.checkForSourcing(dataToReturn.itemList, dataToReturn.isRFSEnabled,dataToReturn.ReasonForNotUsingRFQCode, dataToReturn.PurchaseTypeId);
      console.log("isRFS : ", isRFS);

      await isRFS ? dataToReturn.isRFS = true : dataToReturn.isRFS = false;
    }

    //below code is for split document
    if (dataToReturn.PurchaseTypeId == 1 || dataToReturn.PurchaseTypeId == 3) {
      let isReffered = this.checkForReferenceID(dataToReturn.itemList);

      if (isReffered) {
        dataToReturn.isRFS = false;
        dataToReturn.isRFSEnabled = false;
      }
    }
    return dataToReturn;
  }
  // from backend to frontend
  transformInverse(
    form: SaveMyPurchase,
    storeDropDowns: StoreDropdowns
  ): MyPurchaseForm {
    // instantiate MyPurchaseForm for frontend
    const deliveryAddressSelected = storeDropDowns.DeliveryAddress.find(
      (address) => address.id == form.DeliveryAddressId
    );
    const TenderEndDate = form.TenderEndDate
      ? new Date(form.TenderEndDate)
      : null;
    const TenderStartDate = form.TenderStartDate
      ? new Date(form.TenderStartDate)
      : null;
    let dataToReturn: MyPurchaseForm = {
      generalForm: {
        RO: storeDropDowns.UserList.find(
          (user) => user.id == form.RequestingOfficer
        ) ?? { id: '', value: '' },
        external: this.classification.find((c) => {
          return c.value == form.SecurityClassIdExt;
        }) ?? { id: '', value: '' },
        internal: this.classification.find((c) => {
          return c.value == form.SecurityClassId;
        }) ?? { id: '', value: '' },
        purpose: form.Purpose ?? '',
        purchaseTitle: form.PurchaseTitle ?? '',
        preApproved: { id: '', value: '' },
        typeOfRequisition: TypeOfRequisitions.typeOfRequisitions.find((c) => {
          return c.id == form.TypeOfRequisition;
        }) ?? { id: '', value: '' },
        requisitionOthers: form.OtherTypeOfRequisition,
        permissibleReason: form.PermissibleId ? this.getPermissibleObject(form.PermissibleId, storeDropDowns.PermissibleReason) : null,
        otherPermissibleReasons: form.OtherPermissibleReasons,
        areExcoBoardDocumentsAttached: this.returnExcoBoardDocumentDropdown(form.ExcoBoardDocuments),
        excoBoardDocumentArray: this.returnExcoBoardDocumentArray(form.ExcoBoardDocuments),
        reasonForNotUsingRFQ: this.transformInverseReasonForNotUsingRFQ(form.ReasonForNotUsingRFQCode, form.ReasonForNotUsingRFQ, storeDropDowns),
        blanketPurchaseType: { id: '', value: '' },
        blanketPurchaseDocumentArray: [],
        isDIRP: form.IsDIRP
      },
      ItemList: [],
      TenderItem: {
        DeliveryAddress: deliveryAddressSelected
          ? deliveryAddressSelected
          : form.DeliveryAddressId === null
            ? { id: 'others', value: 'Others' }
            : { id: '', value: '' },
        TargetScheduleDate: form.TargetScheduleDate || null,
        PleaseSpecify: form.OtherDeliveryAddress || '',
      },
      VendorsToSource: [],
      WOCId: form.WaiverOfCompetition?.WOCId as string,
      WoCReason: ((storeDropDowns.WOCReasons as ExtendedWOCReason[]).find(
        (woc) =>
          woc.code == form.WaiverOfCompetition?.WOCCode &&
          !form.WaiverOfCompetition?.isDeleted
      ) as ExtendedWOCReason) ?? {
        id: '',
        content: '',
        tooltip: '',
        code: '',
      },
      DOTPId: form.DOTP?.DOTPId as string,
      DOTPReasonId: form.DOTP?.DOTPReasonId as string,
      DoTPReason: ((storeDropDowns.DOTPReasons as ExtendedDOTPReason[]).find(
        (dotp) =>
          dotp.id == form.DOTP?.DOTPReasonId &&
          !form.DOTP?.isDeleted
      ) as ExtendedDOTPReason) ?? {
        id: '',
        Reason: '',
        code: ''
      },
      isProjectRORequired: false,
      documentNo: form.MPDocumentNumber ?? null,
      parentDocumentNo: form.MPReferenceDocument ?? null,
      documentStatusId: form.DocumentStatusId,
      documentStatus: null,
      documentSubStatusId: form.DocumentSubStatusId ?? null,
      documentSubStatus: null,
      createdBy: form.CreatedBy,
      RFQDeadline: form.RFQDeadline ?? 7,
      Type: form.Type,
      Env: form.Env,
      CreatedOn: form.CreatedOn,
      optionToPurchase: form?.optionToPurchase?.map(x => ({ ...x, isDeleted: x.Strikethrough ?? false, Strikethrough: x.Strikethrough ?? false })) || [],
      TenderEndDate: TenderEndDate ?? null,
      TenderStartDate: TenderStartDate ?? null,
      TenderEndTime: TenderEndDate
        ? `${TenderEndDate.getHours()
          .toString()
          .padStart(2, '0')}:${TenderEndDate.getMinutes()
            .toString()
            .padStart(2, '0')}`
        : null,
      TenderStartTime: TenderStartDate
        ? `${TenderStartDate.getHours()
          .toString()
          .padStart(2, '0')}:${TenderStartDate.getMinutes()
            .toString()
            .padStart(2, '0')}`
        : null,
      PurchaseTypeId: form.PurchaseTypeId,
      CE: form.CE,
      TenderDocumentNumber: form.TenderDocumentNumber,
      WOCFileReferenceNumber: form.WaiverOfCompetition?.isDeleted
        ? null
        : form.WaiverOfCompetition?.FileReferenceNumber,
      WOCJustification: form.WaiverOfCompetition?.isDeleted
        ? null
        : form.WaiverOfCompetition?.WOCJustification,
      DOTPRemarks: form.DOTP?.isDeleted
        ? null
        : form.DOTP?.Remarks,
      DOTPJustification: form.DOTP?.isDeleted
        ? null
        : form.DOTP?.DOTPJustification,
      isRFS: form.isRFS || undefined,
      isRFSEnabled: form.isRFS || false,
      ExchangeRateLock: form.ExchangeRateLock || false,
      rfqSummaryButton: form.rfqSummaryButton
    };

    // setting all the items back to store form
    dataToReturn.ItemList = this.parseItemListData(form.itemList, storeDropDowns)
    // adding vendors back to the list
    for (const vendor of form.vendorsList) {
      let vendorData: Vendor;

      vendorData = {
        ...vendor,
        isDeleted: false,
      };
      dataToReturn.VendorsToSource.push(vendorData);
    }

    dataToReturn.TaggedGroups = this.parseTaggedGroupsData(form.taggedGroups, dataToReturn.VendorsToSource)
    
    dataToReturn.isProjectRORequired = checkIfProjectRORequired(dataToReturn.ItemList.flatMap(x=>x.BudgetAllocation), dataToReturn.ItemList, true)
    return dataToReturn;
  }
  sortTwoItemsBasedOnPurchaseId(a: Item, b: Item) {
    if (a.item.PurchaseItemId === null && b.item.PurchaseItemId !== null) {
      return -1;
    } else if (a.item.PurchaseItemId !== null && b.item.PurchaseItemId === null) {
      return 1;
    } else if (a.item.PurchaseItemId !== null && b.item.PurchaseItemId !== null) {
      return a.item.PurchaseItemId - b.item.PurchaseItemId;
    } else {
      return 0;
    }
  }
  parseItemListData(itemListInput: Item[], storeDropDowns: StoreDropdowns): IItemToRequest[] {
    let itemCount = 1;
    let result: IItemToRequest[] = []
    let itemList = itemListInput.sort((a, b) => this.sortTwoItemsBasedOnPurchaseId(a, b))
    for (const item of itemList) {
      // adding basic information of item here
      let materialGroupData = storeDropDowns.MaterialSubGroup.find((subGroup: any) => subGroup.Code === item.item.MaterialSubGroupId) as ExtendedMaterialSubGroup
      let itemToReturn: IItemToRequest = {
        AddItems: {
          PurchaseItemId: item.item.PurchaseItemId,
          itemId: itemCount,
          displayItemNumber: item.item.ItemNumber ?? itemCount,
          PartModelNumber: item.item.PartModelNumber ?? '',
          IsSML: item.item.IsSML ?? false,
          SMLReasonId: item.item.SMLReasonId
            ? { id: '', value: item.item.SMLReasonId }
            : null,
          isCOTS: item.item.isCOTS
            ? { id: '1', value: 'COTS' }
            : item.item.isCOTS == null
              ? null
              : { id: '2', value: 'Customised' },
          isSingleBudget: item.item.isSingleBudget ?? true,
          ItemDescription: item.item.ItemDescription ?? '',
          PlannedDeliveryDate: item.item.PlannedDeliveryDate ?? new Date(),
          BrandOEMName: item.item.BrandOEMName
            ? { id: item.item.BrandOEMName, value: storeDropDowns.OEM.find(x => x.id == item.item.BrandOEMName)?.value || '' }
            : null,
          BrandOEMNameCustom: item.item.OtherBrandOEMName || null,
          MaterialGroupId: (storeDropDowns.MaterialGroup.find(
            (group: any) => group.Code === item.item.MaterialGroupId
          ) as ExtendedMaterialGroup) ?? { id: null, value: null },
          MaterialSubGroupId: materialGroupData ?? { id: null, value: null },
          IsExemptedAOR: materialGroupData?.IsExemptedAOR,
          PurchaseExecutive: item.item.PurchaseExecutive ?? null,
          WorkmanshipId: item.item.WorkmanshipStandard ?
          { id: WorkmanshipStandard.find(x => x.Name == item.item.WorkmanshipStandard)?.Name || '', value: item.item.WorkmanshipStandard } : null,
          CriticalityId: item.item.Criticality
            ? { id: '', value: item.item.Criticality ?? '' }
            : null,
          SourcingStatusId: item.item.SourcingStatusId ?? null,
          TaggedVendors: item.item.TaggedVendors ?? null,
          ReferenceId: item.item.ReferenceId,
          IsGEWCA: item.item.IsGEWCA ?? false,
          RestrictedITProductPolicy: item.item.RestrictedITProductPolicy ?? null,
          AnnualMaintenanceCost: item.item.AnnualMaintenanceCost ?? null,
          EstimatedUsageId: item.item.EstimatedUsageId ?
          { Name: storeDropDowns.EstimatedUsages.find(x => Number(x.EstimatedUsageId) == item.item.EstimatedUsageId)?.Name || '', EstimatedUsageId: item.item.EstimatedUsageId } : null,
          IsSupportingNonFundingProject: item.item.IsSupportingNonFundingProject ?? null,
          SupportingInformation: item.item.SupportingInformation ?? null,
          IsAdditionalSpaceRequired: item.item.IsAdditionalSpaceRequired ?? null,
        },
        ItemPrice: {
          UnitOfMeasurement: {
            id: '',
            value: item.itemPrice.UnitOfMeasurement ?? '',
          },
          Quantity: item.itemPrice.Quantity ?? 0,
          UnitsPerQuantity: item.itemPrice.Per
            ? parseFloat(item.itemPrice.Per ?? '0.00')
            : 0,
          Currency: { id: '', value: item.itemPrice.CurrencyCode ?? '' },
          UnitPrice: item.itemPrice.EstimateUnitPrice ?? 0,
          ExchangeRate: item.itemPrice.ExchangeRate??0
        },
        BudgetAllocation: item.budgetAllocation ? [
          ...this.backendBudgetToFrontendMapper(
            item.budgetAllocation,
            storeDropDowns,
            itemCount,
            item.item.ItemNumber ?? itemCount
          ),
        ] : [],
        isDeleted: false,
        Strikethrough: item.item.Strikethrough ?? false,
        AddOnFeature: item?.AddOnFeature?.map(x => ({ ...x, isDeleted: x.Strikethrough ?? false, Strikethrough: x.Strikethrough ?? false })) || []
      };
      console.log("itemToReturn == neeraj", itemToReturn)
      result.push(itemToReturn);
      itemCount += 1;
    }

    return result;
  }
  returnExcoBoardDocumentArray(someArray: ExcoBoardDocumentRecord[] | null): ExcoBoardDocumentRecord[] | null {
    if (!someArray) return null
    const results: ExcoBoardDocumentRecord[] = []
    for (const backendExcoBoardRecord of someArray) {
      const result = { ...backendExcoBoardRecord, IsDeleted: false }
      results.push(result)
    }
    return results;
  }

  parseTaggedGroupsData(taggedGroups?: TaggedGroup[] | null, vendorsList?: Vendor[]): TaggedGroups['taggedGroups'] | null {
    if (!taggedGroups) {
      return null;
    }

    return taggedGroups.map((taggedGroup) => {
      const {
        TaggedGroupId,
        ItemNumbers,
        VendorNumbers,
        SourcingStatusId,
        IsDeleted,
        PE,
      } = taggedGroup;

      const vendors: ParsedTaggedGroup['vendors'] =
        vendorsList
          ?.filter((vendor) => {
            return (
              vendor.VendorNumber &&
              !vendor.Strikethrough &&
              !vendor.isDeleted &&
              VendorNumbers.includes(vendor.VendorNumber)
            );
          })
          .map((vendor) => {
            return {
              vendorNumber: vendor.VendorNumber!,
              vendorName: vendor.Name ?? 'Vendor name not found',
            };
          }) ??
        VendorNumbers?.map((vendorNumber) => ({
          vendorNumber: vendorNumber,
          vendorName: 'Vendor name not found',
        })) ??
        [];

      const parsed: ParsedTaggedGroup = {
        taggedGroupId: TaggedGroupId ?? null,
        itemNumbers: ItemNumbers,
        vendors:
          SourcingStatusId === SourcingStatusIdEnum.POTENTIAL_VENDOR_IDENTIFIED
            ? vendors
            : null,
        sourcingStatusId: SourcingStatusId ?? undefined,
        purchaseExecutive: PE!,
        groupStatus: IsDeleted
          ? GroupStatus.PERSISTED_INACTIVE
          : GroupStatus.EXISTING,
      };
      return parsed;
    });
  }

  transformExcoBoardData(form: MyPurchaseForm) {
    if (form.generalForm.areExcoBoardDocumentsAttached.value == 'no' || !form.generalForm.excoBoardDocumentArray) return null
    const results: any[] = []
    for (const record of form.generalForm.excoBoardDocumentArray) {
      results.push({
        EXCOApprovalID: record.EXCOApprovalID ? record.EXCOApprovalID : null,
        ID: record.id,
        isDeleted: record.IsDeleted
      })
    }
    return results;
  }

  //budget allocation
  private backendBudgetToFrontendMapper(
    budgetList: BudgetAllocation[],
    storeDropDowns: StoreDropdowns,
    purchaseItemNo: number,
    displayPurchaseItemNumber: number,
  ): IBudgetAllocForm[] {
    let dataToReturn: IBudgetAllocForm[] = [];
    let budgetNo = 1;
    for (const budget of budgetList) {
      let isBudgeted = this.isBudgetedConverter(
        budget.BudgetType as string,
        budget
      );
      let glAcc = this.getGLAccountByBudgetType(storeDropDowns.GLAcc, budget);
      let budgetToReturn: IBudgetAllocForm = {
        iBudgetId: budgetNo,
        PurchaseItemNo: purchaseItemNo,
        displayPurchaseItemNumber,
        BudgetItemId: budget.ItemBudgetId ?? null,
        Quantity: budget.DistributionQuantity ?? 0,
        BudgetType: {
          id: budget.BudgetType ?? '',
          value: budget.BudgetType ?? '',
        },
        CostCentre: storeDropDowns.CostCentre.find(
          (costCentre) => costCentre.id === budget.OVCostCentre
        ) ?? { id: '', value: '' },
        GLAcc: glAcc ?? { id: '', value: '' },
        OrderNo: {
          id: budget.OVOrderNumber ?? '',
          value: budget.OVOrderNumber ?? '',
        },
        Percentage: budget.DistributionPercent ?? null,
        ProjectBudget: storeDropDowns.ProjectBudget.find(
          (projBudget) => (projBudget.id === budget.PVProjectBudget || projBudget.id === budget.CAUCVCapitalBudget)
        ) ?? { id: '', value: '' },
        ProjectCode: storeDropDowns.ProjectCode.find(
          (projectCode) => projectCode.id === budget.PVProjectCodeName
        ) ?? { id: '', value: '' },
        FundCentre: storeDropDowns.FundCentre.find(
          (fundCentre) => fundCentre.id === budget.CVFundCentre
        ) ?? { id: '', value: '' },
        BudgetExpenditureUsage: {
          id: isBudgeted ?? false,
          value: (isBudgeted ? 'Budgeted' : 'Unbudgeted') ?? '',
        },
        BudgetFrequencyUsage: {
          id: budget.OVIsRecurrent ?? false,
          value: (budget.OVIsRecurrent ? 'Recurrent' : 'Non-recurrent') ?? '',
        },
        ApprovingOfficer: budget.PVApprovingOfficer,
        isDeleted: false,
        Strikethrough: budget.Strikethrough
      };
      dataToReturn.push(budgetToReturn);
      budgetNo += 1;
    }
    return dataToReturn;
  }
  // temp function before database changes
  private isBudgetedConverter(budgetType: string, budget: BudgetAllocation) {
    switch (budgetType?.toLowerCase()) {
      case 'operating':
        return budget.OvIsBudgeted === false ? false : true
      case 'capital auc':
        return budget.CAUCVIsBudgeted === false ? false : true;
      case 'capital':
        return budget.CVIsBudgeted === false ? false : true;
    }

    return false;
  }

  // temp function before database changes

  private getGLAccountByBudgetType(glAcc: any[], budget: BudgetAllocation) {
    return glAcc.find((glacc) => {
      switch (budget.BudgetType?.toLowerCase()) {
        case 'operating':
          return glacc.id === budget.OVGLAccount;
        case 'project':
          return glacc.id === budget.PVGLAccount;
        case 'capital auc':
          return glacc.id === budget.CAUCVGLAccount;
        case 'capital':
          return glacc.id === budget.CVGLAccount;
        default:
          return false;
      }
    });
  }

  

  

  transformToDate(dateObject: Date, time: string | null | undefined) {
    // // set the time
    console.log(dateObject, 'dateObject here');
    const date = structuredClone(dateObject);
    date.setHours(
      time ? Number(time.split(':')[0]) : 0,
      time ? Number(time.split(':')[1]) : 0,
      0,
      0
    );
    return date;
  }
  //transform permissible reason to {id: value} obhect
  getPermissibleObject(id: string, dropdownOptions: any[]) {
    let reason = dropdownOptions.find(x => x.id == id)
    return reason || { id: '', value: '' }
  }

  async returnItemListInSGD(itemList: any[]) {
    let newItemList = structuredClone(itemList)
    newItemList = newItemList.filter(x => !x.isDeleted)
    for (const item of newItemList) {
      item.itemPrice.UnitPrice = await this.currencyService.convertAsyncCurrencyToSGD(item.itemPrice.EstimateUnitPrice, item.itemPrice.CurrencyCode) ?? 0
    }
    return newItemList
  }
  async checkForSourcing(itemList: any, isRFSEnabled: any, reasonSelected: any, purchaseTypeId: number) {
    const filteredItemList = itemList.filter((item: any) => !item.isDeleted && !item.item.Strikethrough);
    let isCOTS = filteredItemList.every((item: any) => item.item.isCOTS === true);
    console.log("isCOTS : ", isCOTS);
    itemList = await this.returnItemListInSGD(filteredItemList)
    let totalPrice = 0;
    if (isCOTS) {
      // Calculate total EstimateUnitPrice
      for (let i = 0; i < itemList.length; i++) {
        if (!itemList[i].isDeleted) {
          totalPrice += (itemList[i].itemPrice.UnitPrice * itemList[i].itemPrice.Quantity) / parseInt(itemList[i].itemPrice.Per);
        }
      }
    }
    //enabling/disabling RFS
    if ((isCOTS && totalPrice > 6000) && ((purchaseTypeId == 1 && reasonSelected === environment.optForRFQReasonCode) || purchaseTypeId == 3)) {
      return true;
    } else {
      return false;
    }
  }


  checkForReferenceID(itemList: any) {
    let isReferenced = itemList.some((item: any) => item.item.ReferenceId);
    return isReferenced;
  }

  calculateTotalAmount(itemList: any) {
    // Initialize total price
    let totalPrice = 0;

    // Calculate total EstimateUnitPrice
    for (let i = 0; i < itemList.length; i++) {
      if (!itemList[i].isDeleted && !itemList[i].Strikethrough) {
        totalPrice += (itemList[i].itemPrice.EstimateUnitPrice * itemList[i].itemPrice.Quantity) / parseInt(itemList[i].itemPrice.Per);
      }
    }
    console.log("totalPrice : ", totalPrice);
    return totalPrice;
  }

  calculateVendorsLength(vendorsArray: any) {
    let filteredItems = vendorsArray.filter((vendor: any) => !vendor.isDeleted && !vendor.Strikethrough);
    return filteredItems.length;
  }
  returnExcoBoardDocumentDropdown(excoBoardDocument: ExcoBoardDocumentRecord[] | null) {
    if (excoBoardDocument == null || excoBoardDocument.length == 0) {
      return {
        id: '2',
        value: 'no'
      }
    }
    return {
      id: '1',
      value: 'yes'
    }
  }

  private transformReasonForNotUsingRFQ(reasonForNotUsingRFQCode: string | null, storeDropdowns: StoreDropdowns) {

    if (!reasonForNotUsingRFQCode) {
      return null;
    }

    const selectedOption = storeDropdowns.ReasonForNotUsingRFQ.find(
      (option) => option.value === reasonForNotUsingRFQCode
    ) as ExtendedReasonForNotUsingRFQ;

    return selectedOption?.Name ?? null;
  }

  private transformInverseReasonForNotUsingRFQ(reasonForNotUsingRFQCode: string | null, reasonForNotUsingRFQName: string | null, storeDropdowns: StoreDropdowns) {
    if (!reasonForNotUsingRFQCode && !reasonForNotUsingRFQName) {
      return { id: '', value: '' };
    }

    let code = reasonForNotUsingRFQCode;
    // Is Opt For RFQ option
    if (!reasonForNotUsingRFQCode && reasonForNotUsingRFQName) {
      code = environment.optForRFQReasonCode;
    }

    const foundOption = storeDropdowns.ReasonForNotUsingRFQ.find(
      (dropdownOption) => dropdownOption.value === code
    );

    return foundOption
      ? { id: foundOption.id, value: foundOption.value }
      : { id: '', value: '' };
  }

  transformAddItem(item:any, index:number,storeDropDowns:StoreDropdowns, purchaseTypeId: number | null){
    let materialGroupData = storeDropDowns.MaterialSubGroup.find((subGroup: any) => subGroup.Code === item.item.MaterialSubGroupId) as ExtendedMaterialSubGroup
    let purchaseType = purchaseTypeId ? PurchaseTypeMap[purchaseTypeId] : null;

    let itemToReturn: IItemToRequest = {
      AddItems: {
        PurchaseItemId: null,
        itemId: index,
        displayItemNumber: index,
        PartModelNumber: item.item.PartModelNumber ?? '',
        IsSML: item.item.IsSML ?? false,
        SMLReasonId: item.item.SMLReasonId
          ? { id: '', value: item.item.SMLReasonId }
          : null,
        isCOTS: item.item.isCOTS
          ? { id: '1', value: 'COTS' }
          : item.item.isCOTS == null
            ? null
            : { id: '2', value: 'Customised' },
        isSingleBudget: true,
        ItemDescription: item.item.ItemDescription ?? '',
        PlannedDeliveryDate: item.item.PlannedDeliveryDate ?? new Date(),
        BrandOEMName: item.item.BrandOEMName
          ? { id: item.item.BrandOEMName, value: storeDropDowns.OEM.find(x => x.id == item.item.BrandOEMName)?.value || '' }
          : null,
        BrandOEMNameCustom: item.item.OtherBrandOEMName || null,
        MaterialGroupId: (storeDropDowns.MaterialGroup.find(
          (group: any) => group.Code === item.item.MaterialGroupId
        ) as ExtendedMaterialGroup) ?? { id: null, value: null },
        MaterialSubGroupId: materialGroupData ?? { id: null, value: null },
        IsExemptedAOR: materialGroupData?.IsExemptedAOR,
        PurchaseExecutive: item.item.PurchaseExecutive ?? null,
        WorkmanshipId: item.item.WorkmanshipStandard ?
          { id: item.item.WorkmanshipStandard, value: WorkmanshipStandard.find(x => x.Name == item.item.WorkmanshipStandard)?.Name || '' } : null,
        CriticalityId: item.item.Criticality
          ? { id: '', value: item.item.Criticality ?? '' }
          : null,
        SourcingStatusId: item.item.SourcingStatusId ?? null,
        TaggedVendors: item.item.TaggedVendors ?? null,
        ReferenceId: item.item.ReferenceId,
        IsGEWCA: item.item.IsGEWCA ?? false,
        RestrictedITProductPolicy: 
            purchaseType &&
            ([
              PurchaseType.RFQ,
              PurchaseType.TENDER,
              PurchaseType.DC,
              PurchaseType.CORPORATE_CARD,
              PurchaseType.PCPO,
              PurchaseType.PERMISSIBLE_COVERING
            ] as string[]).includes(purchaseType) &&
            item.item.RestrictedITProductPolicy?.toLowerCase().includes(
              'cat'
            )
              ? item.item.RestrictedITProductPolicy
              : null,
              AnnualMaintenanceCost: item.item.AnnualMaintenanceCost ?? null,
              EstimatedUsageId: item.item.EstimatedUsageId ?? null,
              IsSupportingNonFundingProject: item.item.IsSupportingNonFundingProject ?? null,
              SupportingInformation: item.item.SupportingInformation ?? null,
              IsAdditionalSpaceRequired: item.item.IsAdditionalSpaceRequired ?? null,

      },
      ItemPrice: {
        UnitOfMeasurement: {
          id: '',
          value: item.itemPrice.UnitOfMeasurement ?? '',
        },
        Quantity: item.itemPrice.Quantity ?? 0,
        UnitsPerQuantity: item.itemPrice.Per
          ? parseFloat(item.itemPrice.Per ?? '1.00')
          : 1.00,
        Currency: { id: '', value: item.itemPrice.CurrencyCode ?? 'SGD' },
        UnitPrice: item.itemPrice.EstimateUnitPrice ?? 0,
        ExchangeRate: item.itemPrice.ExchangeRate ??0,
      },
      BudgetAllocation: item.budgetAllocation ? [
        ...this.backendBudgetToFrontendMapperAddItem(
          item.budgetAllocation,
          storeDropDowns,
          index,
          item.item.ItemNumber ?? index,
          item.itemPrice.Quantity
        ),
      ] : [],
      
      isDeleted: false,
      Strikethrough: item.item.Strikethrough ?? false,
      AddOnFeature: item.AddOnFeature ? [...this.AddonFeatureAddItem(item.AddOnFeature,index)] : []
    };
      console.log("new item array",itemToReturn)
      return itemToReturn;
      // this.store.dispatch(addItemtoMyPurchase({ form: itemToReturn }));
  }

  private backendBudgetToFrontendMapperAddItem(
    budgetList: BudgetAllocation[],
    storeDropDowns: StoreDropdowns,
    purchaseItemNo: number,
    displayPurchaseItemNumber: number,
    ItemQuantity : number
  ): IBudgetAllocForm[] {
    let dataToReturn: IBudgetAllocForm[] = [];
    let budgetNo = 1;
    for (const budget of budgetList) {
      let isBudgeted = this.isBudgetedConverter(
        budget.BudgetType as string,
        budget
      );
      let glAcc = this.getGLAccountByBudgetType(storeDropDowns.GLAcc, budget);
      let budgetToReturn: IBudgetAllocForm = {
        iBudgetId: budgetNo,
        PurchaseItemNo: purchaseItemNo,
        displayPurchaseItemNumber,
        BudgetItemId: null,
        Quantity: ItemQuantity ?? 0, //added itemQuantity as in upload template no budget split and always consider as quantity
        BudgetType: {
          id: budget.BudgetType ?? '',
          value: budget.BudgetType ?? '',
        },
        CostCentre: storeDropDowns.CostCentre.find(
          (costCentre) => costCentre.id === String(budget.OVCostCentre)
        ) ?? { id: '', value: '' },
        GLAcc: glAcc ?? { id: '', value: '' },
        OrderNo: {
          id: budget.OVOrderNumber ?? '',
          value: budget.OVOrderNumber ?? '',
        },
        Percentage: null,
        ProjectBudget: storeDropDowns.ProjectBudget.find(
          (projBudget) => (projBudget.id === String(budget.PVProjectBudget) || projBudget.id === String(budget.CAUCVCapitalBudget))
        ) ?? { id: '', value: '' },
        ProjectCode: storeDropDowns.ProjectCode.find(
          (projectCode) => projectCode.id === String(budget.PVProjectCodeName)
        ) ?? { id: '', value: '' },
        FundCentre: storeDropDowns.FundCentre.find(
          (fundCentre) => fundCentre.id === String(budget.CVFundCentre)
        ) ?? { id: '', value: '' },
        BudgetExpenditureUsage: {
          id: isBudgeted == false ? false : true,
          value: isBudgeted == false ? 'Unbudgeted' : 'Budgeted'
        },
        BudgetFrequencyUsage: {
          id: budget.OVIsRecurrent ?? false,
          value: (budget.OVIsRecurrent ? 'Recurrent' : 'Non-recurrent'),
        },
        ApprovingOfficer: budget.PVApprovingOfficer ?? null,
        isDeleted: false,
        Strikethrough: false
      };
      dataToReturn.push(budgetToReturn);
      budgetNo += 1;
    }
    return dataToReturn;
  }

  private AddonFeatureAddItem(AddOnFeature:AddOn[],index:number){
    let dataAddOn: AddOn[] = [];
    for (const addOn of AddOnFeature) {
      let budgetToReturn: AddOn = {
          "AddOnTypeId": addOn.AddOnTypeId,
          "ItemAddOnId": null,
          "PurchaseItemId": null,
          "isDeleted": false,
          "Quantity": addOn.Quantity,
          "DurationYears": addOn.DurationYears,
          "DurationMonths": addOn.DurationMonths,
          "UnitOfMeasurements": addOn.UnitOfMeasurements,
          "CurrencyCode": addOn.CurrencyCode,
          "EstimatedUnitPrice": addOn.EstimatedUnitPrice,
          "Strikethrough": false,
          "itemId": index,
          "displayItemNumber": index
      };

      dataAddOn.push(budgetToReturn);
    }
    return dataAddOn;
  } 
}
